/**
 * Helpers for question answer options (legacy strings and tagged objects).
 *
 * @module questionOptions
 */

export type OptionObject = {
  text: string;
  key?: string;
  tags?: string[];
  tag_confidence?: string | null;
  [key: string]: unknown;
};

export type OptionValue = string | OptionObject;

/** Diagnostic tags on a tagged option object. */
export function optionTags(option: OptionValue): string[] {
  if (
    typeof option === 'object' &&
    option !== null &&
    Array.isArray((option as OptionObject).tags)
  ) {
    return (option as OptionObject).tags as string[];
  }
  return [];
}

/** Tag confidence on a tagged option object. */
export function optionTagConfidence(option: OptionValue): string | null {
  if (typeof option === 'object' && option !== null && 'tag_confidence' in option) {
    const confidence = (option as OptionObject).tag_confidence;
    return confidence != null && String(confidence).trim() !== ''
      ? String(confidence)
      : null;
  }
  return null;
}

/** Parse API options while preserving tagged option objects. */
export function parseQuestionOptions(options: unknown): OptionValue[] {
  if (!options) {
    return [];
  }
  if (Array.isArray(options)) {
    return options as OptionValue[];
  }
  if (typeof options === 'string') {
    try {
      const parsed = JSON.parse(options);
      if (Array.isArray(parsed)) {
        return parsed as OptionValue[];
      }
    } catch {
      // ignore
    }
  }
  return [options as OptionValue];
}

/** Display text for a legacy string or tagged option object. */
export function optionText(option: OptionValue): string {
  return typeof option === 'object' && option !== null && 'text' in option
    ? String((option as OptionObject).text)
    : String(option);
}

/** Letter key (A, B, …) for a tagged option, or index-based fallback. */
export function optionKey(option: unknown, index: number): string {
  if (typeof option === 'object' && option !== null && 'key' in option) {
    const key = String((option as OptionObject).key ?? '').trim();
    if (key) {
      return key;
    }
  }
  return String.fromCodePoint(65 + index);
}

/** Normalize API options to display strings for preview and lists. */
export function normalizeQuestionOptions(options: unknown): string[] {
  if (!options) {
    return [];
  }
  return parseQuestionOptions(options).map((o) => optionText(o));
}

/** Whether an option matches expected answer (by text or letter key). */
export function isOptionExpected(
  option: unknown,
  index: number,
  expected: string | string[] | undefined,
): boolean {
  if (expected === undefined || expected === null || expected === '') {
    return false;
  }
  const text = optionText(option as OptionValue);
  const key = optionKey(option, index);
  if (Array.isArray(expected)) {
    return expected.some(
      (e) => e === text || e === key || e.toLowerCase() === text.toLowerCase(),
    );
  }
  return (
    expected === text ||
    expected === key ||
    expected.toLowerCase() === text.toLowerCase()
  );
}
